import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';

// eslint-disable-next-line import/no-cycle
import { ActionButtonNodeView } from './node-view';

export const ACTION_BUTTON_NODE_NAME = 'actionButton';

export interface ActionButtonAttributes {
  buttonId: string;
  value: string;
  link: string;
  backgroundColor: string;
  textColor: string;
  borderRadius: string;
  fullWidth: boolean;
  alignment: 'left' | 'center' | 'right';
  fontFamily: string;
}

export type ActionButtonUpdateAttributes = (
  attrs: Partial<ActionButtonAttributes>,
) => void;

export type ActionButtonOptions = {
  /**
   * Called when a user clicks an Action Button node.
   *
   * Use `helpers.updateAttributes(...)` to persist changes back into the editor
   * (e.g. on dialog submit).
   */
  onClick?: (
    attrs: ActionButtonAttributes,
    helpers: {
      updateAttributes: ActionButtonUpdateAttributes;
    },
  ) => void;
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    actionButton: {
      /**
       * Insert an action button
       */
      insertActionButton: (
        attrs: Partial<ActionButtonAttributes>,
      ) => ReturnType;
    };
  }
}

export const ActionButton = Node.create<ActionButtonOptions>({
  name: ACTION_BUTTON_NODE_NAME,
  group: 'block',
  content: 'inline*',
  atom: true,
  draggable: true,

  addOptions() {
    return {
      onClick: undefined,
    };
  },

  addAttributes() {
    return {
      buttonId: {
        default: undefined,
        parseHTML: (element): string | null =>
          element.getAttribute('data-buttonid'),
        renderHTML: (attributes): Record<string, string> => ({
          'data-buttonid': attributes.buttonId as string,
        }),
      },
      value: {
        default: 'Button',
        parseHTML: (element): string | null =>
          element.getAttribute('data-value'),
        renderHTML: (attributes): Record<string, string> => ({
          'data-value': attributes.value as string,
        }),
      },
      link: {
        default: '',
        parseHTML: (element): string | null =>
          element.getAttribute('data-link'),
        renderHTML: (attributes): Record<string, string> => ({
          'data-link': attributes.link as string,
        }),
      },
      borderRadius: {
        default: '8px',
        parseHTML: (element): string | null =>
          element.getAttribute('data-borderradius'),
        renderHTML: (attributes): Record<string, string> => ({
          'data-borderradius': attributes.borderRadius as string,
        }),
      },
      backgroundColor: {
        default: '#000000',
        parseHTML: (element): string | null =>
          element.getAttribute('data-backgroundcolor'),
        renderHTML: (attributes): Record<string, string> => ({
          'data-backgroundcolor': (
            attributes.backgroundColor as string
          ).toUpperCase(),
        }),
      },
      textColor: {
        default: '#ffffff',
        parseHTML: (element): string | null =>
          element.getAttribute('data-textcolor'),
        renderHTML: (attributes): Record<string, string> => ({
          'data-textcolor': (attributes.textColor as string).toUpperCase(),
        }),
      },
      fullWidth: {
        default: true,
        parseHTML: (element): boolean | null => {
          const raw = element.getAttribute('data-fullwidth');
          if (raw == null) return null;
          return raw === 'true';
        },
        renderHTML: (attributes): Record<string, string> => ({
          'data-fullwidth': String(attributes.fullWidth),
        }),
      },
      alignment: {
        default: 'center',
        parseHTML: (element): string =>
          element.getAttribute('data-alignment') ?? 'center',
        renderHTML: (attributes): Record<string, string> => ({
          'data-alignment': attributes.alignment as string,
        }),
      },
      fontFamily: {
        default: 'helvetica',
        parseHTML: (element): string | null =>
          element.getAttribute('data-fontfamily'),
        renderHTML: (attributes): Record<string, string> => ({
          'data-fontfamily': attributes.fontFamily as string,
        }),
      },
    };
  },

  parseHTML() {
    return [
      { tag: `div[data-type="${this.name}"]` },
      { tag: 'div[data-name="actionButton"]' },
    ];
  },

  renderHTML({ HTMLAttributes, node }) {
    const attrs = node.attrs as ActionButtonAttributes;
    const alignment = attrs.alignment ?? 'center';
    const fontFamily = attrs.fontFamily ?? 'helvetica';

    // Wx-compatible wrapper: data-name and data-type for wx recognition
    const wrapperAttrs = mergeAttributes(HTMLAttributes, {
      'data-name': 'actionButton',
      'data-type': 'link',
    });

    // Wx uses table-based structure for email client compatibility
    const tableStyle =
      'border-collapse:separate;line-height:100%;width:100%;margin:0;padding-top:4px;padding-bottom:4px;';
    const tdStyle = `border:none;border-radius:${attrs.borderRadius};cursor:auto;background-color:transparent !important;text-align:${alignment} !important;`;
    const width = attrs.fullWidth ? '100%' : 'auto';
    const anchorStyle = `display:block;background:${attrs.backgroundColor};color:${attrs.textColor};font-family:${fontFamily}, sans-serif;font-size:16px;font-weight:bold;line-height:120%;margin:0;text-decoration:none;text-transform:none;padding:10px 0px;mso-padding-alt:0px;border-radius:${attrs.borderRadius};width:${width};text-align:${alignment};`;

    return [
      'div',
      wrapperAttrs,
      [
        'div',
        {},
        [
          'table',
          {
            border: '0',
            cellpadding: '0',
            cellspacing: '0',
            role: 'presentation',
            style: tableStyle,
          },
          [
            'tbody',
            {},
            [
              'tr',
              {},
              [
                'td',
                {
                  class: 'button',
                  align: alignment,
                  role: 'presentation',
                  style: tdStyle,
                  valign: 'middle',
                },
                [
                  'a',
                  {
                    href: attrs.link || '#',
                    class: 'button',
                    style: anchorStyle,
                  },
                  attrs.value,
                ],
              ],
            ],
          ],
        ],
      ],
    ];
  },

  addCommands() {
    return {
      insertActionButton:
        (attrs: Partial<ActionButtonAttributes>) =>
        ({ commands }) => {
          const buttonId =
            attrs.buttonId ??
            `btn-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          return commands.insertContent({
            type: this.name,
            attrs: {
              buttonId,
              value: attrs.value ?? 'Apply to job',
              link: attrs.link ?? '',
              backgroundColor: attrs.backgroundColor ?? '#2600FF',
              textColor: attrs.textColor ?? '#ffffff',
              borderRadius: attrs.borderRadius ?? '8px',
              fullWidth: attrs.fullWidth ?? true,
              alignment: attrs.alignment ?? 'center',
              fontFamily: attrs.fontFamily ?? 'helvetica',
            },
          });
        },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(ActionButtonNodeView, {
      contentDOMElementTag: 'span',
    });
  },
});
